/**
 * ValoresReferenciaSheet - Panel lateral para gestionar valores de referencia de un examen
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useValoresReferencia, useDeleteValorReferencia } from "@/hooks/use-ordenes";
import type { ExamenTipoListItem, ValorReferencia } from "@/hooks/use-ordenes";
import { daysToYears } from "./valor-referencia-utils";
import type { ValorReferenciaFormValues } from "./valor-referencia-utils";
import { ValorReferenciaFormDialog } from "./valor-referencia-form-dialog";

// =============================================================================
// Types
// =============================================================================

interface ValoresReferenciaSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  examen: ExamenTipoListItem | null;
}

// =============================================================================
// Helpers
// =============================================================================

function getSexBadgeVariant(sexo: string): "default" | "secondary" | "outline" {
  switch (sexo) {
    case "M":
      return "default";
    case "F":
      return "secondary";
    default:
      return "outline";
  }
}

// =============================================================================
// Component
// =============================================================================

export function ValoresReferenciaSheet({
  open,
  onOpenChange,
  examen,
}: ValoresReferenciaSheetProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  // Form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDefaults, setEditingDefaults] = useState<
    Partial<ValorReferenciaFormValues> | undefined
  >(undefined);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  // Data
  const { data, isLoading } = useValoresReferencia(examen?.id ?? 0, {
    enabled: open && !!examen,
  });
  const valores = data?.results ?? [];

  const deleteMutation = useDeleteValorReferencia({
    onSuccess: () => {
      toast.success(t("ordenes.valoresReferencia.toast.deleted"));
      setDeleteTarget(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
      setDeleteTarget(null);
    },
  });

  // Handlers
  const handleAdd = () => {
    setEditingId(null);
    setEditingDefaults(undefined);
    setFormOpen(true);
  };

  const handleEdit = (vr: ValorReferencia) => {
    setEditingId(vr.id);
    setEditingDefaults({
      sexo: vr.sexo,
      edad_min_anios: daysToYears(vr.edad_min_dias),
      edad_max_anios: daysToYears(vr.edad_max_dias),
      valor_min: vr.valor_min,
      valor_max: vr.valor_max,
      valor_texto: vr.valor_texto ?? "",
      unidad: vr.unidad ?? "",
      descripcion: vr.descripcion ?? "",
    });
    setFormOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget != null) {
      deleteMutation.mutate(deleteTarget);
    }
  };

  // Render helpers
  const formatAgeRange = (vr: ValorReferencia) => {
    const min = daysToYears(vr.edad_min_dias);
    const max = daysToYears(vr.edad_max_dias);
    if (min != null && max != null) {
      return `${min} - ${max} ${t("ordenes.valoresReferencia.years")}`;
    }
    if (min != null) {
      return `≥ ${min} ${t("ordenes.valoresReferencia.years")}`;
    }
    if (max != null) {
      return `≤ ${max} ${t("ordenes.valoresReferencia.years")}`;
    }
    return null;
  };

  const formatValueRange = (vr: ValorReferencia) => {
    if (vr.valor_min != null && vr.valor_max != null) {
      return `${vr.valor_min} - ${vr.valor_max}`;
    }
    if (vr.valor_min != null) {
      return `≥ ${vr.valor_min}`;
    }
    if (vr.valor_max != null) {
      return `≤ ${vr.valor_max}`;
    }
    if (vr.valor_texto) {
      return vr.valor_texto;
    }
    return null;
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          className="sm:max-w-[600px] overflow-y-auto"
          data-testid="valores-referencia-sheet"
        >
          <SheetHeader>
            <SheetTitle>{t("ordenes.valoresReferencia.title")}</SheetTitle>
            <SheetDescription>
              {examen ? `${examen.nombre} (${examen.codigo})` : ""}
            </SheetDescription>
          </SheetHeader>

          {isLoading ? (
            <div className="mt-6 space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : valores.length === 0 ? (
            /* Empty state */
            <div className="mt-6 flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">
                {t("ordenes.valoresReferencia.noRanges")}
              </p>
              <Button onClick={handleAdd}>
                <Plus className="mr-2 h-4 w-4" />
                {t("ordenes.valoresReferencia.addFirstRange")}
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {/* Add button */}
              <div className="flex justify-end">
                <Button size="sm" onClick={handleAdd}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("ordenes.valoresReferencia.addRange")}
                </Button>
              </div>

              <Separator />

              {/* List of reference values */}
              <div className="space-y-3">
                {valores.map((vr) => {
                  const ageRange = formatAgeRange(vr);
                  const valueRange = formatValueRange(vr);

                  return (
                    <div
                      key={vr.id}
                      className="flex items-start justify-between gap-2 p-3 border rounded-md text-sm"
                    >
                      <div className="flex-1 space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={getSexBadgeVariant(vr.sexo)}>
                            {t(`ordenes.valoresReferencia.sex.${vr.sexo}`)}
                          </Badge>
                          {ageRange && (
                            <span className="text-muted-foreground text-xs">
                              {t("ordenes.valoresReferencia.ageRange")}: {ageRange}
                            </span>
                          )}
                        </div>
                        {valueRange && (
                          <p className="font-medium">
                            {valueRange}
                            {vr.unidad ? ` ${vr.unidad}` : ""}
                          </p>
                        )}
                        {vr.descripcion && (
                          <p className="text-muted-foreground text-xs">{vr.descripcion}</p>
                        )}
                      </div>

                      {/* Row actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(vr)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget(vr.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Form dialog for create/edit */}
      {examen && (
        <ValorReferenciaFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          examenTipoId={examen.id}
          valorReferenciaId={editingId}
          defaultValues={editingDefaults}
          onSuccess={() => setFormOpen(false)}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t("ordenes.valoresReferencia.confirmDelete")}
        description={t("ordenes.valoresReferencia.confirmDeleteDesc")}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}
